import React, { useEffect, useMemo } from "react";
import ScreenCard from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import PdfAssetViewer from "../../components/PdfAssetViewer";
import { useQuery } from "@apollo/client/react";
import { useI18n } from "../../hooks/i18n-hooks";
import { capitalizeFirstLetter } from "../../utils";
import { intlFormat } from "date-fns";
import { FIND_MY_SALARIES, GET_SALARY } from "../../graphql/salaries";
import apolloClient from "../../configs/graphql.config";
import { useSnackbar } from "../../hooks/snackbar";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { isScrollEnabledForPdfComponent } from "../../utils/pdf-scroll";

type SalaryDetailsScreenProps = StackScreenProps<RootStackParamList, "SalaryDetails">;

const SalaryDetailsScreen = ({ route, navigation }: SalaryDetailsScreenProps) => {
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();

    const { salaryId } = useMemo(() => route.params, [route.params]);
    const { data, error, loading } = useQuery(GET_SALARY, {
        variables: { salaryId },
        skip: !salaryId,
    });

    const salary = data?.getSalary;
    const assetId = salary?.salaryAsset?.id || "";
    const filename = useMemo(
        () =>
            salary && salary.month && salary.year
                ? i18n.t("screens.user.salaries.filename_salary", {
                      month: capitalizeFirstLetter(
                          intlFormat(new Date(2000, salary.month - 1), {
                              month: "long",
                          }),
                      ),
                      year: salary.year,
                  })
                : "",
        [i18n, salary],
    );

    useEffect(() => {
        // check if salary still exists,
        // backend return null if salary does not exist or is not accessible by the current user
        if (data && data.getSalary === null) {
            void apolloClient.refetchQueries({
                include: [FIND_MY_SALARIES],
            });
            // notify user
            showSnackbar({
                text: i18n.t(`salary.salary_not_found`),
                mode: "warning",
            });
            if (navigation.canGoBack()) {
                navigation.goBack();
            } else {
                navigation.popTo("Home", { screen: "SalariesScreen" });
            }
        }
    }, [data, i18n, navigation, showSnackbar]);
    return (
        <LoadingBoundary loading={loading && !data} error={error}>
            <ScreenCard
                isContentContainerFlex
                scrollEnabled={isScrollEnabledForPdfComponent("ScreenCard")}
            >
                {assetId && (
                    <PdfAssetViewer
                        assetId={assetId}
                        filename={filename}
                        waitingDescription={i18n.t("screens.user.salaries.wait_salary_asset")}
                        scrollEnabled={isScrollEnabledForPdfComponent("PdfViewer")}
                    />
                )}
            </ScreenCard>
        </LoadingBoundary>
    );
};

export default SalaryDetailsScreen;
